import {Separator} from "@/components/ui/separator";

import {cn} from "@/lib/utils";

import type {SafelistingsKpi} from "../types";
import {DeltaPill} from "./delta-pill";
import {KpiCard, type KpiCardDensity} from "./kpi-card";

type KpiCardSafelistingsProps = {
  kpi: SafelistingsKpi;
  icon?: React.ElementType;
  density?: KpiCardDensity;
  selected?: boolean;
  onSelect?: () => void;
};

/**
 * Dual-metric tile. `active_days_in_period` is the operational signal
 * (occupancy of the safelisting feature), while `requests_in_period` is
 * the volume of new requests issued.
 */
export function KpiCardSafelistings({
  kpi,
  icon,
  density = "default",
  selected,
  onSelect,
}: KpiCardSafelistingsProps) {
  const isCompact = density === "compact";
  const n = isCompact ? "text-3xl" : "text-4xl";
  const subLabel = "text-muted-foreground text-xs font-semibold uppercase tracking-wide";

  return (
    <KpiCard
      label={kpi.label ?? "Safelistings"}
      icon={icon}
      density={density}
      selected={selected}
      onSelect={onSelect}
    >
      <div className="flex flex-col gap-1">
        <span className={subLabel}>Active days</span>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className={cn("text-foreground min-w-0 font-bold tracking-tight tabular-nums", n)}>
            {kpi.active_days_in_period.toLocaleString()}
          </span>
          <DeltaPill
            value={kpi.delta_pct_active_days}
            size={isCompact ? "sm" : "default"}
            className="shrink-0"
          />
        </div>
      </div>
      <div className={cn("mt-auto flex flex-col gap-1", !isCompact && "mt-2")}>
        <Separator className={isCompact ? "my-0.5" : undefined} />
        <div className="flex items-center justify-between gap-3">
          <span className={subLabel}>New requests</span>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-1">
            <span className="text-foreground min-w-0 text-lg font-bold tabular-nums">
              {kpi.requests_in_period.toLocaleString()}
            </span>
            <DeltaPill
              value={kpi.delta_pct_requests}
              size={isCompact ? "sm" : "default"}
              className="shrink-0"
            />
          </div>
        </div>
      </div>
    </KpiCard>
  );
}
